// overview/shared — gemensamma byggstenar för OverviewDialogens fyra
// vy-lägen (flat/detailed/tree/compact). Extraherad ur OverviewDialog
// (#6 i PRE_RELEASE_REVIEW_100).

import type { WorkflowBlock, Connector } from '@/types/workflow';
import { cn } from '@/lib/utils';



/** Tiny block card for the overview */
export function OverviewBlock({ block }: { block: WorkflowBlock }) {
  const isCondition = block.type === 'condition';
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium whitespace-nowrap",
        isCondition && "border-dashed"
      )}
      style={{
        borderColor: block.color,
        backgroundColor: `${block.color}12`,
        color: block.color,
      }}
    >
      <span className="truncate max-w-[140px]">{block.config.name}</span>
    </div>
  );
}

/** Arrow between blocks (horizontal) */
export function Arrow() {
  return (
    <svg width="24" height="16" viewBox="0 0 24 16" className="flex-shrink-0 opacity-40">
      <line x1="0" y1="8" x2="18" y2="8" stroke="currentColor" strokeWidth="1.5" />
      <polyline points="14,3 20,8 14,13" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/** Arrow between blocks (vertical, for detailed view) */
export function VerticalArrow() {
  return (
    <svg width="16" height="20" viewBox="0 0 16 20" className="flex-shrink-0 opacity-40 mx-auto">
      <line x1="8" y1="0" x2="8" y2="14" stroke="currentColor" strokeWidth="1.5" />
      <polyline points="3,10 8,16 13,10" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/** Cross-group connectors section (shared across views) */
export function CrossGroupConnectors({
  connectors,
  resolveName,
  resolveColor,
}: {
  connectors: Connector[];
  resolveName: (id: string) => string;
  resolveColor: (id: string) => string;
}) {
  if (connectors.length === 0) return null;
  return (
    <div className="rounded-xl border-2 border-dashed border-muted-foreground/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-semibold text-muted-foreground">
          Cross-entity connections
        </span>
        <span className="text-xs text-muted-foreground">
          ({connectors.length})
        </span>
      </div>
      <div className="space-y-1.5">
        {connectors.map(c => (
          <div key={c.id} className="flex items-center gap-2 text-xs">
            <span className="font-medium" style={{ color: resolveColor(c.fromId) }}>
              {resolveName(c.fromId)}
            </span>
            <Arrow />
            <span className="font-medium" style={{ color: resolveColor(c.toId) }}>
              {resolveName(c.toId)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── FLAT VIEW (existing) ───────────────────────────────────────────

export function DetailedBlock({ block }: { block: WorkflowBlock }) {
  const isCondition = block.type === 'condition';
  return (
    <div
      className={cn(
        "rounded-lg border p-3",
        isCondition && "border-dashed"
      )}
      style={{
        borderColor: block.color,
        backgroundColor: `${block.color}08`,
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: block.color }} />
        <span className="text-sm font-semibold" style={{ color: block.color }}>
          {block.config.name}
        </span>
      </div>
      {block.config.description && (
        <p className="text-xs text-muted-foreground whitespace-pre-line ml-4 leading-relaxed">
          {block.config.description}
        </p>
      )}
    </div>
  );
}

